import React, { useEffect, useState } from "react";
import DashImagesEditor from "../components/DashImagesEditor";
import api, { fmtErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import StatusBadge from "../components/StatusBadge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Card, CardContent } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Plus, KeyRound, Pencil, Trash2, RotateCcw, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

const ROLES = ["SUPER_ADMIN", "TEMPLE_ADMIN", "SUPERVISOR", "COUNTER_USER", "VERIFICATION_USER"];
const COUNTER_TYPES = ["PROTOCOL", "ADVANCED_BOOKING", "CURRENT_BOOKING", "GENERAL", "VERIFICATION", "SAREE_RECEIVING", "SAREE_CONTRACT_SALES", "SAREE_DEVASTHANAM_SALES", "ANNAPRASADAM_ISSUE", "ANNAPRASADAM_VERIFY"];
// Counter picker label: name + type, so a saree user is not given a protocol counter by mistake.
// Super Admin gets every temple's counters / shifts: a user can only use their own temple's.
const ofTemple = (list, tid) => (tid ? list.filter((x) => x.temple_id === tid) : list);
const counterLabel = (c) => `${c.name} · ${(c.counter_type || "").replace(/_/g, " ").toLowerCase()}`;
const WA_TPLS = [
  ["BOOKING_SUBMITTED", "Protocol — Advanced/Current Booking Submitted"],
  ["APPROVED", "Protocol — Booking Approved"],
  ["REJECTED", "Protocol — Booking Rejected"],
  ["TICKET_ISSUED", "Protocol — Ticket Issued"],
  ["DARSHAN_COMPLETED", "Protocol — Darshan Completed"],
  ["CANCELLATION", "Protocol — Cancellation"],
  ["SAREE_ISSUED", "Sarees — Receiving Counter (Token Issued)"],
  ["SAREE_SOLD", "Sarees — Sale Invoice"],
  ["HUNDIAL_REPORT", "Hundial — Opening Report (PDF)"],
];

const WA_EVENT_LABEL = Object.fromEntries(WA_TPLS);

// Live template status from Meta for this temple's saved template names (save changes first).
function WaTemplateStatus({ templeId }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const check = async () => {
    setBusy(true);
    try { setData((await api.get(`/temples/${templeId}/wa-templates`)).data); }
    catch (err) { toast.error(fmtErr(err)); } finally { setBusy(false); }
  };
  const badge = (st) => {
    const ok = st === "APPROVED";
    const wait = st === "PENDING" || st === "IN_APPEAL";
    return <span className={`text-[10px] font-bold rounded px-1.5 py-0.5 border ${ok ? "text-green-700 bg-green-50 border-green-200" : wait ? "text-amber-700 bg-amber-50 border-amber-200" : "text-red-700 bg-red-50 border-red-200"}`}>{ok ? "APPROVED" : wait ? "IN REVIEW" : st}</span>;
  };
  return (
    <div className="mb-2" data-testid="wa-template-status">
      <Button type="button" size="sm" variant="outline" onClick={check} disabled={busy}>{busy ? "Checking with Meta…" : "Check template status with Meta"}</Button>
      {data && (
        <table className="w-full text-[11px] mt-2 border border-[#E5D9C5]">
          <thead><tr className="bg-gold-50 text-left text-gold-700 uppercase"><th className="p-1">Event</th><th className="p-1">Template</th><th className="p-1">Status</th><th className="p-1">Header</th><th className="p-1">Variables</th></tr></thead>
          <tbody>
            {data.events.length === 0 && <tr><td colSpan={5} className="p-2 text-slate-400">No template names saved yet</td></tr>}
            {data.events.map((e) => (
              <tr key={e.event} className="border-t border-[#F1EADB]">
                <td className="p-1">{WA_EVENT_LABEL[e.event] || e.event}</td>
                <td className="p-1 font-mono">{e.name}</td>
                <td className="p-1">{badge(e.status)}{e.rejected_reason && e.rejected_reason !== "NONE" ? <span className="block text-red-600">{e.rejected_reason}</span> : null}</td>
                <td className="p-1">{e.header || "-"}</td>
                <td className={`p-1 ${e.variables != null && e.variables !== e.expected_variables ? "text-red-600 font-bold" : ""}`}>{e.variables ?? "-"} / needs {e.expected_variables}</td>
              </tr>))}
          </tbody>
        </table>)}
      {data && <p className="text-[10px] text-slate-500 mt-1">Templates in this WhatsApp account: {data.templates.map((t) => `${t.name} (${t.language}, ${t.status})`).join(" · ") || "none"}</p>}
    </div>
  );
}

function useList(path) {
  const [rows, setRows] = useState([]);
  const load = () => api.get(path).then((r) => setRows(r.data)).catch((e) => toast.error(fmtErr(e)));
  useEffect(() => { load(); }, [path]); // eslint-disable-line
  return [rows, load];
}

function SimpleTable({ head, children, testid }) {
  return (
    <Card className="shadow-sm"><CardContent className="overflow-x-auto p-4">
      <table className="w-full text-sm" data-testid={testid}>
        <thead><tr className="text-left text-[11px] uppercase tracking-wider text-gold-700 border-b border-[#E5D9C5]">
          {head.map((h) => <th key={h} className="py-2 pr-4">{h}</th>)}
        </tr></thead>
        <tbody>{children}</tbody>
      </table>
    </CardContent></Card>
  );
}

function Temples() {
  const { user } = useAuth();
  const [rows, load] = useList("/temples");
  const [apps, setApps] = useState({});
  const loadApps = () => api.get("/apps/info").then((r) => setApps(Object.fromEntries((Array.isArray(r.data) ? r.data : []).map((m) => [m.temple_id, m])))).catch(() => {});
  useEffect(() => { if (user.role === "SUPER_ADMIN") loadApps(); }, []); // eslint-disable-line
  const uploadApp = async (t, file) => {
    if (!file) return;
    const version = window.prompt(`Version for ${t.name}'s app (e.g. 1.3):`, "1.3");
    if (version === null) return;
    try {
      await api.put(`/admin/apps/${t.temple_id}`, file, { headers: { "Content-Type": "application/vnd.android.package-archive", "X-App-Version": version } });
      toast.success(`App published for ${t.name}`);
      loadApps();
    } catch (err) { toast.error(fmtErr(err)); }
  };
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ temple_id: "", name: "", name_telugu: "", location: "", district: "", state: "" });
  const create = async (e) => {
    e.preventDefault();
    try { await api.post("/temples", f); toast.success("Temple created"); setOpen(false); load(); }
    catch (err) { toast.error(fmtErr(err)); }
  };
  const toggle = async (t) => {
    try { await api.patch(`/temples/${t.temple_id}/status`, { status: t.status === "ACTIVE" ? "INACTIVE" : "ACTIVE" }); load(); }
    catch (err) { toast.error(fmtErr(err)); }
  };
  const [editT, setEditT] = useState(null);
  const [ef, setEf] = useState({});
  const [adOriginal, setAdOriginal] = useState(undefined);
  const openEdit = async (listed) => {
    // Load the latest saved settings, so saving never writes back stale values.
    let t = listed;
    try { t = (await api.get("/temples")).data.find((x) => x.temple_id === listed.temple_id) || listed; } catch { /* use listed */ }
    setEditT(t);
    setEf({ name: t.name, name_telugu: t.name_telugu, location: t.location, district: t.district, state: t.state,
            pincode: t.pincode, contact: t.contact, email: t.email, website: t.website, whatsapp_number: t.whatsapp_number,
            wa_phone_number_id: t.wa_phone_number_id || "", wa_business_account_id: t.wa_business_account_id || "",
            wa_access_token: t.wa_access_token || "", wa_templates: t.wa_templates || {},
            wa_tpl_names: t.wa_tpl_names || {}, wa_tpl_lang: t.wa_tpl_lang || "en_US", wa_tpl_pdf: t.wa_tpl_pdf || {},
            report_header: t.report_header || "", report_footer: t.report_footer || "",
            sign_label_1: t.sign_label_1 || "", sign_label_2: t.sign_label_2 || "", sign_label_3: t.sign_label_3 || "",
            header_title: t.header_title || "", footer_text: t.footer_text || "",
            logo: t.logo || "", logo_right: t.logo_right || "", reason: "" });
    // The advertisement is loaded separately (large image); it is only saved when changed.
    setAdOriginal(undefined);
    api.get(`/temples/${t.temple_id}/ticket-ad`).then((r) => {
      setAdOriginal(r.data.ticket_ad || "");
      setEf((p) => ({ ...p, ticket_ad: r.data.ticket_ad || "" }));
    }).catch(() => {});
  };
  const saveEdit = async () => {
    const body = { ...ef };
    if (body.ticket_ad === undefined || body.ticket_ad === adOriginal) delete body.ticket_ad; // unchanged: never overwrite
    try { await api.put(`/temples/${editT.temple_id}`, body); toast.success("Temple updated (audited)"); setEditT(null); load(); }
    catch (err) { toast.error(fmtErr(err)); }
  };
  return (
    <div className="space-y-3">
      {user.role === "SUPER_ADMIN" && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="bg-maroon-700 hover:bg-maroon-800 text-white" data-testid="temple-add-btn"><Plus className="w-4 h-4 mr-1" /> Add Temple</Button></DialogTrigger>
          <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto" data-testid="temple-dialog">
            <DialogHeader><DialogTitle className="font-heading text-maroon-900">New Temple</DialogTitle></DialogHeader>
            <form onSubmit={create} className="grid grid-cols-2 gap-3">
              <div><Label>Temple ID *</Label><Input value={f.temple_id} onChange={(e) => setF({ ...f, temple_id: e.target.value })} required data-testid="temple-id-input" placeholder="T003" /></div>
              <div><Label>Name *</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} required data-testid="temple-name-input" /></div>
              <div><Label>Name (Telugu)</Label><Input value={f.name_telugu} onChange={(e) => setF({ ...f, name_telugu: e.target.value })} /></div>
              <div><Label>Location</Label><Input value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} /></div>
              <div><Label>District</Label><Input value={f.district} onChange={(e) => setF({ ...f, district: e.target.value })} /></div>
              <div><Label>State</Label><Input value={f.state} onChange={(e) => setF({ ...f, state: e.target.value })} /></div>
              <Button type="submit" className="col-span-2 bg-maroon-700 hover:bg-maroon-800 text-white" data-testid="temple-save-btn">Save Temple</Button>
            </form>
          </DialogContent>
        </Dialog>
      )}
      {user.role === "SUPER_ADMIN" && (
        <Dialog open={!!editT} onOpenChange={() => setEditT(null)}>
          <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="temple-edit-dialog">
            <DialogHeader><DialogTitle className="font-heading text-maroon-900">Edit Temple {editT?.temple_id}</DialogTitle></DialogHeader>
            {editT && (
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Temple Name *</Label><Input value={ef.name || ""} onChange={(e) => setEf({ ...ef, name: e.target.value })} data-testid="temple-edit-name" /></div>
                <div><Label className="text-xs">Name (Telugu)</Label><Input value={ef.name_telugu || ""} onChange={(e) => setEf({ ...ef, name_telugu: e.target.value })} /></div>
                <div><Label className="text-xs">Location</Label><Input value={ef.location || ""} onChange={(e) => setEf({ ...ef, location: e.target.value })} /></div>
                <div><Label className="text-xs">District</Label><Input value={ef.district || ""} onChange={(e) => setEf({ ...ef, district: e.target.value })} /></div>
                <div><Label className="text-xs">State</Label><Input value={ef.state || ""} onChange={(e) => setEf({ ...ef, state: e.target.value })} /></div>
                <div><Label className="text-xs">PIN Code</Label><Input value={ef.pincode || ""} onChange={(e) => setEf({ ...ef, pincode: e.target.value })} /></div>
                <div><Label className="text-xs">Contact Number</Label><Input value={ef.contact || ""} onChange={(e) => setEf({ ...ef, contact: e.target.value })} data-testid="temple-edit-contact" /></div>
                <div><Label className="text-xs">Email</Label><Input value={ef.email || ""} onChange={(e) => setEf({ ...ef, email: e.target.value })} /></div>
                <div><Label className="text-xs">Website URL</Label><Input value={ef.website || ""} onChange={(e) => setEf({ ...ef, website: e.target.value })} data-testid="temple-edit-website" /></div>
                <div><Label className="text-xs">WhatsApp Number / ID</Label><Input value={ef.whatsapp_number || ""} onChange={(e) => setEf({ ...ef, whatsapp_number: e.target.value })} data-testid="temple-edit-whatsapp" /></div>
                <div className="col-span-2 mt-1 border-t border-[#E5D9C5] pt-3">
                  <p className="text-xs font-bold uppercase tracking-wider text-gold-700 mb-2">Meta WhatsApp Business Cloud API (optional — enables real WhatsApp sending)</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="text-xs">Phone Number ID</Label><Input value={ef.wa_phone_number_id || ""} onChange={(e) => setEf({ ...ef, wa_phone_number_id: e.target.value })} data-testid="temple-edit-wa-phone-id" placeholder="e.g. 123456789012345" /></div>
                    <div><Label className="text-xs">Business Account ID</Label><Input value={ef.wa_business_account_id || ""} onChange={(e) => setEf({ ...ef, wa_business_account_id: e.target.value })} data-testid="temple-edit-wa-waba-id" placeholder="e.g. 987654321098765" /></div>
                    <div className="col-span-2"><Label className="text-xs">Access Token</Label>
                      <Input type="password" value={ef.wa_access_token || ""} onChange={(e) => setEf({ ...ef, wa_access_token: e.target.value })} data-testid="temple-edit-wa-token"
                        placeholder={ef.wa_access_token?.startsWith("••••") ? "Token saved — type a new one to replace" : "EAAG... (stored securely, shown masked)"} />
                    </div>
                  </div>
                </div>
                <div className="col-span-2 mt-1 border-t border-[#E5D9C5] pt-3">
                  <p className="text-xs font-bold uppercase tracking-wider text-gold-700 mb-1">WhatsApp Message Templates (this temple only — blank = default message)</p>
                  <p className="text-[10px] text-slate-400 mb-2">Placeholders: {"{name} {txn_id} {token_no} {temple} {amount} {darshan} {status} {reason} {ticket_no} {saree_type} {rate} {category} {rect_no} {total}"} — sent only to that transaction's devotee mobile.</p>
                  <div className="grid grid-cols-1 gap-2 max-h-64 overflow-y-auto pr-1">
                    {WA_TPLS.map(([k, lbl]) => (
                      <div key={k}>
                        <Label className="text-xs">{lbl}</Label>
                        <Textarea rows={2} value={(ef.wa_templates || {})[k] || ""}
                          onChange={(e) => setEf({ ...ef, wa_templates: { ...(ef.wa_templates || {}), [k]: e.target.value } })}
                          data-testid={`wa-tpl-${k.toLowerCase()}`} />
                      </div>
                    ))}
                  </div>
                </div>
                <div className="col-span-2 mt-1 border-t border-[#E5D9C5] pt-3">
                  <p className="text-xs font-bold uppercase tracking-wider text-gold-700 mb-1">WhatsApp Approved Template Names (Meta / OmniCloud — for real sending)</p>
                  <p className="text-[10px] text-slate-400 mb-2">Approved template name per event — when set (with Phone Number ID + Access Token above), details are auto-sent on that event. Variables are positional: {"{{1}}"} devotee name, {"{{2}}"} transaction / token no, {"{{3}}"} = status / darshan / ticket no / saree type / amount (by event), and for Saree Token {"{{4}}"} = quantity. Ticket Issue sends the Access Card JPG as the image header. Tick "PDF header" when the approved template has a Document header — a PDF (logos, details, QR) is generated and attached. Blank = mock mode.</p>
                  <WaTemplateStatus templeId={editT.temple_id} />
                  <div className="grid grid-cols-2 gap-2">
                    <div><Label className="text-xs">Language code</Label><Input value={ef.wa_tpl_lang || "en_US"} onChange={(e) => setEf({ ...ef, wa_tpl_lang: e.target.value })} data-testid="wa-tpl-lang" placeholder="en_US" /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 mt-2 max-h-48 overflow-y-auto pr-1">
                    {WA_TPLS.map(([k, lbl]) => (
                      <div key={k}><Label className="text-xs">{lbl}</Label>
                        <Input value={(ef.wa_tpl_names || {})[k] || ""}
                          onChange={(e) => setEf({ ...ef, wa_tpl_names: { ...(ef.wa_tpl_names || {}), [k]: e.target.value } })}
                          data-testid={`wa-tplname-${k.toLowerCase()}`} placeholder="e.g. protocol_access_card" />
                        {["BOOKING_SUBMITTED", "APPROVED", "SAREE_ISSUED", "SAREE_SOLD", "HUNDIAL_REPORT"].includes(k) && (
                          <label className="flex items-center gap-1 text-[11px] text-slate-600 mt-0.5">
                            <input type="checkbox" checked={(ef.wa_tpl_pdf || {})[k] !== false}
                              onChange={(e) => setEf({ ...ef, wa_tpl_pdf: { ...(ef.wa_tpl_pdf || {}), [k]: e.target.checked } })} data-testid={`wa-tplpdf-${k.toLowerCase()}`} />
                            PDF header (attach generated PDF)
                          </label>)}</div>
                    ))}
                  </div>
                </div>
                <div className="col-span-2 mt-1 border-t border-[#E5D9C5] pt-3">
                  <p className="text-xs font-bold uppercase tracking-wider text-gold-700 mb-2">Report Settings (PDF header / footer — A4)</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="text-xs">Report Header Line</Label><Input value={ef.report_header || ""} onChange={(e) => setEf({ ...ef, report_header: e.target.value })} data-testid="temple-edit-report-header" placeholder="Shown under temple name" /></div>
                    <div><Label className="text-xs">Report Footer Text</Label><Input value={ef.report_footer || ""} onChange={(e) => setEf({ ...ef, report_footer: e.target.value })} data-testid="temple-edit-report-footer" placeholder="Bottom of every PDF page" /></div>
                    <div><Label className="text-xs">Signature Label 1</Label><Input value={ef.sign_label_1 || ""} onChange={(e) => setEf({ ...ef, sign_label_1: e.target.value })} placeholder="Counter Signature" /></div>
                    <div><Label className="text-xs">Signature Label 2</Label><Input value={ef.sign_label_2 || ""} onChange={(e) => setEf({ ...ef, sign_label_2: e.target.value })} placeholder="Superintendent" /></div>
                    <div><Label className="text-xs">Signature Label 3</Label><Input value={ef.sign_label_3 || ""} onChange={(e) => setEf({ ...ef, sign_label_3: e.target.value })} placeholder="Executive Officer" /></div>
                    {[["logo", "Temple Logo — left (header, reports, cards)"], ["logo_right", "Second Logo — right (ticket & saree cards)"]].map(([key, label]) => (
                      <div key={key}>
                        <Label className="text-xs">{label} · PNG/JPG, max 1 MB</Label>
                        <div className="flex items-center gap-2">
                          {ef[key] && <img src={ef[key]} alt="logo" className="w-9 h-9 object-contain border border-gold-300 rounded" data-testid={`temple-edit-${key}-preview`} />}
                          <label className="text-xs text-maroon-700 underline cursor-pointer" data-testid={`temple-edit-${key}-upload`}>
                            {ef[key] ? "Replace" : "Upload"}
                            <input type="file" accept="image/png,image/jpeg" className="hidden" onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (!file) return;
                              if (file.size > 1024 * 1024) { toast.error("Logo too large (max 1 MB)"); return; }
                              const reader = new FileReader();
                              reader.onload = () => setEf((p) => ({ ...p, [key]: reader.result }));
                              reader.readAsDataURL(file);
                            }} />
                          </label>
                          {ef[key] && <button type="button" className="text-xs text-red-600 underline" onClick={() => setEf((p) => ({ ...p, [key]: "" }))}>Remove</button>}
                        </div>
                      </div>
                    ))}
                    <div className="col-span-2 rounded-lg border border-dashed border-gold-400 bg-gold-50/40 p-3" data-testid="temple-edit-ticket-ad">
                      <Label className="text-xs">Ticket Advertisement · printed in each Protocol / Saree ticket (A4 · 2 tickets per page) · PNG/JPG, max 2 MB · best a wide banner about 19 x 4.5 cm (e.g. 1800 x 430 px)</Label>
                      <div className="flex items-center gap-3 mt-1.5">
                        {ef.ticket_ad
                          ? <img src={ef.ticket_ad} alt="advertisement" className="h-16 w-48 object-contain bg-white border border-gold-300 rounded" data-testid="temple-edit-ticket-ad-preview" />
                          : <div className="h-16 w-48 rounded border border-dashed border-slate-300 flex items-center justify-center text-[11px] text-slate-400">No advertisement</div>}
                        <label className="text-xs text-maroon-700 underline cursor-pointer" data-testid="temple-edit-ticket-ad-upload">
                          {ef.ticket_ad ? "Replace" : "Upload"}
                          <input type="file" accept="image/png,image/jpeg" className="hidden" onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            if (file.size > 2 * 1024 * 1024) { toast.error("Advertisement too large (max 2 MB)"); return; }
                            const reader = new FileReader();
                            reader.onload = () => setEf((p) => ({ ...p, ticket_ad: reader.result }));
                            reader.readAsDataURL(file);
                          }} />
                        </label>
                        {ef.ticket_ad && <button type="button" className="text-xs text-red-600 underline" onClick={() => setEf((p) => ({ ...p, ticket_ad: "" }))}>Remove</button>}
                      </div>
                    </div>
                    {user.role === "SUPER_ADMIN" && <DashImagesEditor temple={editT} />}
                  </div>
                </div>
                <div className="col-span-2 mt-1 border-t border-[#E5D9C5] pt-3">
                  <p className="text-xs font-bold uppercase tracking-wider text-gold-700 mb-2">App Branding Override (optional — overrides the global branding for this temple's users)</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="text-xs">Header Title</Label><Input value={ef.header_title || ""} onChange={(e) => setEf({ ...ef, header_title: e.target.value })} data-testid="temple-edit-header-title" placeholder="e.g. Temple Management System" /></div>
                    <div><Label className="text-xs">Footer Text</Label><Input value={ef.footer_text || ""} onChange={(e) => setEf({ ...ef, footer_text: e.target.value })} data-testid="temple-edit-footer-text" placeholder="e.g. Powered by …" /></div>
                  </div>
                </div>
                <div className="col-span-2"><Label className="text-xs">Reason (optional)</Label><Input value={ef.reason || ""} onChange={(e) => setEf({ ...ef, reason: e.target.value })} /></div>
                <div className="col-span-2 flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setEditT(null)}>Close</Button>
                  <Button onClick={saveEdit} className="bg-maroon-700 hover:bg-maroon-800 text-white" data-testid="temple-edit-save-btn">Save Changes</Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      )}
      <SimpleTable head={["Temple ID", "Name", "Location", "District", "Website", "Status", ...(user.role === "SUPER_ADMIN" ? ["Android App", "Action"] : [])]} testid="temples-table">
        {rows.map((t) => (
          <tr key={t.temple_id} className="border-b border-[#F1EADB]" data-testid={`temple-row-${t.temple_id}`}>
            <td className="py-2 pr-4 font-mono font-bold">{t.temple_id}</td>
            <td className="py-2 pr-4">{t.name}<br /><span className="text-xs text-slate-500">{t.name_telugu}</span></td>
            <td className="py-2 pr-4">{t.location}</td>
            <td className="py-2 pr-4">{t.district}</td>
            <td className="py-2 pr-4 text-xs">
              {t.website ? <a href={t.website} target="_blank" rel="noreferrer" className="text-maroon-700 underline" data-testid={`temple-url-${t.temple_id}`}>{t.website}</a> : "—"}
            </td>
            <td className="py-2 pr-4"><StatusBadge status={t.status} /></td>
            {user.role === "SUPER_ADMIN" && (
              <td className="py-2 pr-4 text-xs" data-testid={`temple-app-${t.temple_id}`}>
                {apps[t.temple_id] ? <p className="font-semibold text-green-700">v{apps[t.temple_id].version || "?"}<span className="block font-normal text-slate-500">{new Date(apps[t.temple_id].uploaded_at).toLocaleDateString("en-IN")} · {(apps[t.temple_id].size / 1048576).toFixed(1)} MB</span></p>
                  : <p className="text-slate-400">Not published</p>}
                <label className="text-maroon-700 underline cursor-pointer">{apps[t.temple_id] ? "Replace APK" : "Upload APK"}
                  <input type="file" accept=".apk,application/vnd.android.package-archive" className="hidden" onChange={(e) => uploadApp(t, e.target.files?.[0])} />
                </label>
              </td>
            )}
            {user.role === "SUPER_ADMIN" && (
              <td className="py-2 flex gap-1">
                <Button size="sm" variant="ghost" title="Edit temple" onClick={() => openEdit(t)} data-testid={`temple-edit-${t.temple_id}`}><Pencil className="w-4 h-4 text-saffron-600" /></Button>
                <Button size="sm" variant="outline" onClick={() => toggle(t)} data-testid={`temple-toggle-${t.temple_id}`}>{t.status === "ACTIVE" ? "Disable" : "Enable"}</Button>
              </td>
            )}
          </tr>
        ))}
      </SimpleTable>
    </div>
  );
}

import { PAGE_GROUPS, defaultPagesFor, pageRoleOk } from "../lib/pages";

function PagePicker({ value, onChange, role, testidPrefix }) {
  const toggle = (k) => onChange(value.includes(k) ? value.filter((x) => x !== k) : [...value, k]);
  const toggleGroup = (applicable) => {
    const keys = applicable.map((p) => p.key);
    const allOn = keys.length > 0 && keys.every((k) => value.includes(k));
    onChange(allOn ? value.filter((k) => !keys.includes(k)) : [...new Set([...value, ...keys])]);
  };
  return (
    <div className="col-span-2">
      <Label className="text-xs font-bold uppercase tracking-wider text-gold-700">Page Access *</Label>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-1.5">
        {PAGE_GROUPS.map((g) => {
          const applicable = g.pages.filter((p) => !role || p.roles.includes(role));
          return (
            <div key={g.module} className="border border-[#E5D9C5] rounded-md p-2.5 bg-gold-50/40">
              <label className="flex items-center gap-2 text-sm font-semibold text-maroon-800 cursor-pointer">
                <input type="checkbox" className="w-4 h-4 accent-[#800020]"
                  checked={applicable.length > 0 && applicable.every((p) => value.includes(p.key))}
                  onChange={() => toggleGroup(applicable)} data-testid={`${testidPrefix}-group-${g.module.toLowerCase()}`} />
                {g.label}
              </label>
              <div className="mt-1.5 ml-2 space-y-1">
                {g.pages.map((p) => {
                  const ok = !role || p.roles.includes(role);
                  return (
                    <label key={p.key} className={`flex items-center gap-2 text-xs cursor-pointer ${ok ? "text-slate-700" : "text-slate-400"}`}>
                      <input type="checkbox" disabled={!ok} className="w-3.5 h-3.5 accent-[#800020] disabled:opacity-40"
                        checked={value.includes(p.key)} onChange={() => toggle(p.key)}
                        data-testid={`${testidPrefix}-${p.key.toLowerCase()}`} />
                      {p.label}
                    </label>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
      <p className="text-[10px] text-slate-400 mt-1">The user's sidebar will show only the selected pages. Some pages are limited by role.</p>
    </div>
  );
}

function Users() {
  const { user } = useAuth();
  const [rows, load] = useList("/users");
  const [meta, setMeta] = useState({ temples: [], counters: [], shifts: [] });
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ username: "", full_name: "", password: "", role: "", temple_id: "", counter_id: "", shift_id: "", mobile: "", email: "", pages: [] });
  const [editU, setEditU] = useState(null);
  const [eu, setEu] = useState({});
  const openEdit = (u) => setEditU(u) || setEu({ full_name: u.full_name, role: u.role, counter_id: u.counter_id || "", shift_id: u.shift_id || "", pages: u.pages?.length ? u.pages : defaultPagesFor(u.role, u.modules), mobile: u.mobile || "" });
  const saveEdit = async () => {
    if (!eu.pages?.length) { toast.error("Select at least one page"); return; }
    try { await api.patch(`/users/${editU.id}`, eu); toast.success("User updated (audited)"); setEditU(null); load(); }
    catch (err) { toast.error(fmtErr(err)); }
  };
  useEffect(() => { api.get("/meta/form-data").then((r) => setMeta(r.data)).catch(() => {}); }, []);
  const create = async (e) => {
    e.preventDefault();
    if (!f.pages.length) { toast.error("Select at least one page"); return; }
    try { await api.post("/users", f); toast.success("User created"); setOpen(false); load(); }
    catch (err) { toast.error(fmtErr(err)); }
  };
  const toggle = async (u) => {
    try { await api.patch(`/users/${u.id}/status`, { status: u.status === "ACTIVE" ? "INACTIVE" : "ACTIVE" }); load(); }
    catch (err) { toast.error(fmtErr(err)); }
  };
  const [pwUser, setPwUser] = useState(null);
  const [pw, setPw] = useState({ p1: "", p2: "", show: false });
  const resetPw = (u) => { setPwUser(u); setPw({ p1: "", p2: "", show: false }); };
  const savePw = async () => {
    if (pw.p1.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (pw.p1 !== pw.p2) { toast.error("Passwords do not match"); return; }
    try { await api.post(`/users/${pwUser.id}/reset-password`, { password: pw.p1 }); toast.success(`Password changed for ${pwUser.username} (audited)`); setPwUser(null); }
    catch (err) { toast.error(fmtErr(err)); }
  };
  return (
    <div className="space-y-3">
      <Dialog open={!!pwUser} onOpenChange={(o) => !o && setPwUser(null)}>
        <DialogContent className="max-w-md" data-testid="reset-pw-dialog">
          <DialogHeader><DialogTitle className="font-heading text-maroon-900 flex items-center gap-2"><KeyRound className="w-5 h-5" /> Reset Password</DialogTitle></DialogHeader>
          {pwUser && (
            <form className="space-y-3" onSubmit={(e) => { e.preventDefault(); savePw(); }}>
              <p className="text-sm text-slate-600">Set a new password for <b className="text-maroon-900">{pwUser.full_name}</b> <span className="font-mono text-xs">({pwUser.username})</span>.</p>
              <div><Label className="text-xs">New Password *</Label>
                <div className="relative">
                  <Input type={pw.show ? "text" : "password"} autoFocus autoComplete="new-password" value={pw.p1} onChange={(e) => setPw({ ...pw, p1: e.target.value })} data-testid="reset-pw-new" />
                  <button type="button" className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500" onClick={() => setPw({ ...pw, show: !pw.show })} title={pw.show ? "Hide" : "Show"}>
                    {pw.show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button>
                </div>
                <p className={`text-[11px] mt-1 ${pw.p1.length >= 6 ? "text-green-700" : "text-slate-500"}`}>At least 6 characters{pw.p1 ? ` · ${pw.p1.length} entered` : ""}</p></div>
              <div><Label className="text-xs">Confirm Password *</Label>
                <Input type={pw.show ? "text" : "password"} autoComplete="new-password" value={pw.p2} onChange={(e) => setPw({ ...pw, p2: e.target.value })} data-testid="reset-pw-confirm" />
                {pw.p2 && <p className={`text-[11px] mt-1 ${pw.p1 === pw.p2 ? "text-green-700" : "text-red-600"}`}>{pw.p1 === pw.p2 ? "Passwords match" : "Passwords do not match"}</p>}</div>
              <div className="flex justify-end gap-2 pt-1">
                <Button type="button" variant="outline" onClick={() => setPwUser(null)}>Cancel</Button>
                <Button type="submit" disabled={pw.p1.length < 6 || pw.p1 !== pw.p2} className="bg-maroon-700 hover:bg-maroon-800 text-white" data-testid="reset-pw-save">Change Password</Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild><Button className="bg-maroon-700 hover:bg-maroon-800 text-white" data-testid="user-add-btn"><Plus className="w-4 h-4 mr-1" /> Add User</Button></DialogTrigger>
        <DialogContent data-testid="user-dialog">
          <DialogHeader><DialogTitle className="font-heading text-maroon-900">New User</DialogTitle></DialogHeader>
          <form onSubmit={create} className="grid grid-cols-2 gap-3">
            <div><Label>Username *</Label><Input value={f.username} onChange={(e) => setF({ ...f, username: e.target.value })} required data-testid="user-username-input" /></div>
            <div><Label>Full Name *</Label><Input value={f.full_name} onChange={(e) => setF({ ...f, full_name: e.target.value })} required data-testid="user-fullname-input" /></div>
            <div><Label>Password *</Label><Input type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} required data-testid="user-password-input" /></div>
            <div><Label>Role *</Label>
              <Select value={f.role} onValueChange={(v) => setF({ ...f, role: v, pages: f.pages.filter((p) => pageRoleOk(p, v)) })}>
                <SelectTrigger data-testid="user-role-select"><SelectValue placeholder="Select role" /></SelectTrigger>
                <SelectContent>{ROLES.filter((r) => user.role === "SUPER_ADMIN" || r !== "SUPER_ADMIN").map((r) => <SelectItem key={r} value={r}>{r.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
              </Select></div>
            {user.role === "SUPER_ADMIN" && f.role !== "SUPER_ADMIN" && (
              <div><Label>Temple *</Label>
                <Select value={f.temple_id} onValueChange={(v) => setF({ ...f, temple_id: v, counter_id: "", shift_id: "" })}>
                  <SelectTrigger data-testid="user-temple-select"><SelectValue placeholder="Select temple" /></SelectTrigger>
                  <SelectContent>{meta.temples.map((t) => <SelectItem key={t.temple_id} value={t.temple_id}>{t.name}</SelectItem>)}</SelectContent>
                </Select></div>
            )}
            <div><Label>Counter</Label>
              <Select value={f.counter_id} onValueChange={(v) => setF({ ...f, counter_id: v === "NONE" ? "" : v })}>
                <SelectTrigger data-testid="user-counter-select"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent><SelectItem value="NONE">None</SelectItem>{ofTemple(meta.counters, f.temple_id || user.temple_id).map((c) => <SelectItem key={c.id} value={c.id}>{counterLabel(c)}</SelectItem>)}</SelectContent>
              </Select></div>
            <div><Label>Shift</Label>
              <Select value={f.shift_id} onValueChange={(v) => setF({ ...f, shift_id: v === "NONE" ? "" : v })}>
                <SelectTrigger data-testid="user-shift-select"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent><SelectItem value="NONE">None</SelectItem>{ofTemple(meta.shifts, f.temple_id || user.temple_id).map((s) => <SelectItem key={s.id} value={s.id}>{s.name} ({s.start}–{s.end})</SelectItem>)}</SelectContent>
              </Select></div>
            <div><Label>Mobile * <span className="text-[10px] font-normal text-slate-500">(for OTP password reset)</span></Label><Input required inputMode="numeric" pattern="(\+?91)?[6-9][0-9]{9}" title="10-digit mobile number (used for OTP password reset)" value={f.mobile} onChange={(e) => setF({ ...f, mobile: e.target.value })} placeholder="10-digit mobile" data-testid="user-mobile-input" /></div>
            <PagePicker value={f.pages} onChange={(p) => setF({ ...f, pages: p })} role={f.role} testidPrefix="user-page" />
            <Button type="submit" className="col-span-2 bg-maroon-700 hover:bg-maroon-800 text-white" data-testid="user-save-btn">Save User</Button>
          </form>
        </DialogContent>
      </Dialog>
      <SimpleTable head={["Username", "Full Name", "Role", "Page Access", "Temple", "Status", "Last Login", "Actions"]} testid="users-table">
        {rows.map((u) => (
          <tr key={u.id} className="border-b border-[#F1EADB]" data-testid={`user-row-${u.username}`}>
            <td className="py-2 pr-4 font-mono text-xs">{u.username}</td>
            <td className="py-2 pr-4">{u.full_name}
              <span className={`block text-[11px] ${u.mobile ? "text-slate-500" : "text-red-600 font-semibold"}`} data-testid={`user-mobile-${u.username}`}>{u.mobile || "⚠ No mobile — add one for OTP"}</span></td>
            <td className="py-2 pr-4 text-xs font-semibold">{u.role.replace(/_/g, " ")}</td>
            <td className="py-2 pr-4 text-xs" title={(u.pages?.length ? u.pages : defaultPagesFor(u.role, u.modules)).join(", ")}>{(u.pages?.length ? u.pages : defaultPagesFor(u.role, u.modules)).length} pages · {(u.modules || []).join(", ")}</td>
            <td className="py-2 pr-4">{u.temple_id || "—"}</td>
            <td className="py-2 pr-4"><StatusBadge status={u.status} /></td>
            <td className="py-2 pr-4 text-xs text-slate-500">{u.last_login ? new Date(u.last_login).toLocaleString("en-IN") : "Never"}</td>
            <td className="py-2 flex gap-1">
              <Button size="sm" variant="ghost" title="Edit user / module access" onClick={() => openEdit(u)} data-testid={`user-edit-${u.username}`}><Pencil className="w-4 h-4 text-saffron-600" /></Button>
              <Button size="sm" variant="outline" onClick={() => toggle(u)} data-testid={`user-toggle-${u.username}`}>{u.status === "ACTIVE" ? "Disable" : "Enable"}</Button>
              <Button size="sm" variant="ghost" onClick={() => resetPw(u)} data-testid={`user-reset-${u.username}`}><KeyRound className="w-4 h-4" /></Button>
            </td>
          </tr>
        ))}
      </SimpleTable>

      <Dialog open={!!editU} onOpenChange={() => setEditU(null)}>
        <DialogContent data-testid="user-edit-dialog">
          <DialogHeader><DialogTitle className="font-heading text-maroon-900">Edit User — {editU?.username}</DialogTitle></DialogHeader>
          {editU && (
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Full Name</Label><Input value={eu.full_name || ""} onChange={(e) => setEu({ ...eu, full_name: e.target.value })} data-testid="edit-user-fullname" /></div>
              <div><Label className="text-xs">Mobile * (for OTP password reset)</Label><Input inputMode="numeric" value={eu.mobile || ""} onChange={(e) => setEu({ ...eu, mobile: e.target.value })} placeholder="10-digit mobile" data-testid="edit-user-mobile" /></div>
              <div><Label className="text-xs">Role</Label>
                <Select value={eu.role || ""} onValueChange={(v) => setEu({ ...eu, role: v, pages: (eu.pages || []).filter((p) => pageRoleOk(p, v)) })}>
                  <SelectTrigger data-testid="edit-user-role"><SelectValue /></SelectTrigger>
                  <SelectContent>{ROLES.filter((r) => user.role === "SUPER_ADMIN" || r !== "SUPER_ADMIN").map((r) => <SelectItem key={r} value={r}>{r.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
                </Select></div>
              <div><Label className="text-xs">Counter</Label>
                <Select value={eu.counter_id || "NONE"} onValueChange={(v) => setEu({ ...eu, counter_id: v === "NONE" ? "" : v })}>
                  <SelectTrigger data-testid="edit-user-counter"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="NONE">None</SelectItem>{ofTemple(meta.counters, editU.temple_id).map((c) => <SelectItem key={c.id} value={c.id}>{counterLabel(c)}</SelectItem>)}</SelectContent>
                </Select></div>
              <div><Label className="text-xs">Shift</Label>
                <Select value={eu.shift_id || "NONE"} onValueChange={(v) => setEu({ ...eu, shift_id: v === "NONE" ? "" : v })}>
                  <SelectTrigger data-testid="edit-user-shift"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="NONE">None</SelectItem>{ofTemple(meta.shifts, editU.temple_id).map((s) => <SelectItem key={s.id} value={s.id}>{s.name} ({s.start}–{s.end})</SelectItem>)}</SelectContent>
                </Select></div>
              <PagePicker value={eu.pages || []} onChange={(p) => setEu({ ...eu, pages: p })} role={eu.role} testidPrefix="edit-user-page" />
              <div className="col-span-2 flex justify-end gap-2">
                <Button variant="outline" onClick={() => setEditU(null)}>Close</Button>
                <Button onClick={saveEdit} className="bg-maroon-700 hover:bg-maroon-800 text-white" data-testid="edit-user-save-btn">Save Changes</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Counters() {
  const { user } = useAuth();
  const [rows, load] = useList("/counters");
  const [meta, setMeta] = useState({ temples: [] });
  const [f, setF] = useState({ name: "", counter_type: "PROTOCOL", temple_id: "" });
  useEffect(() => { api.get("/meta/form-data").then((r) => setMeta(r.data)).catch(() => {}); }, []);
  const create = async (e) => {
    e.preventDefault();
    try { await api.post("/counters", f); toast.success("Counter created"); setF({ name: "", counter_type: "PROTOCOL", temple_id: "" }); load(); }
    catch (err) { toast.error(fmtErr(err)); }
  };
  return (
    <div className="space-y-3">
      <Card className="shadow-sm"><CardContent className="p-4">
        <form onSubmit={create} className="grid grid-cols-2 md:grid-cols-4 gap-3 items-end">
          <div><Label className="text-xs">Counter Name *</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} required data-testid="counter-name-input" /></div>
          <div><Label className="text-xs">Type</Label>
            <Select value={f.counter_type} onValueChange={(v) => setF({ ...f, counter_type: v })}>
              <SelectTrigger data-testid="counter-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>{COUNTER_TYPES.map((c) => <SelectItem key={c} value={c}>{c.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
            </Select></div>
          {user.role === "SUPER_ADMIN" && (
            <div><Label className="text-xs">Temple *</Label>
              <Select value={f.temple_id} onValueChange={(v) => setF({ ...f, temple_id: v })}>
                <SelectTrigger data-testid="counter-temple-select"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{meta.temples.map((t) => <SelectItem key={t.temple_id} value={t.temple_id}>{t.name}</SelectItem>)}</SelectContent>
              </Select></div>
          )}
          <Button type="submit" className="bg-maroon-700 hover:bg-maroon-800 text-white" data-testid="counter-save-btn"><Plus className="w-4 h-4 mr-1" /> Add Counter</Button>
        </form>
      </CardContent></Card>
      <SimpleTable head={["Name", "Type", "Temple", "Status"]} testid="counters-table">
        {rows.map((c) => (
          <tr key={c.id} className="border-b border-[#F1EADB]">
            <td className="py-2 pr-4 font-semibold">{c.name}</td>
            <td className="py-2 pr-4 text-xs">{c.counter_type.replace(/_/g, " ")}</td>
            <td className="py-2 pr-4">{c.temple_id}</td>
            <td className="py-2"><StatusBadge status={c.status} /></td>
          </tr>
        ))}
      </SimpleTable>
    </div>
  );
}

function Shifts() {
  const { user } = useAuth();
  const [rows, load] = useList("/shifts");
  const [meta, setMeta] = useState({ temples: [] });
  const [f, setF] = useState({ name: "", start: "", end: "", temple_id: "" });
  useEffect(() => { api.get("/meta/form-data").then((r) => setMeta(r.data)).catch(() => {}); }, []);
  const create = async (e) => {
    e.preventDefault();
    try { await api.post("/shifts", f); toast.success("Shift created"); setF({ name: "", start: "", end: "", temple_id: "" }); load(); }
    catch (err) { toast.error(fmtErr(err)); }
  };
  return (
    <div className="space-y-3">
      <Card className="shadow-sm"><CardContent className="p-4">
        <form onSubmit={create} className="grid grid-cols-2 md:grid-cols-5 gap-3 items-end">
          <div><Label className="text-xs">Shift Name *</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} required data-testid="shift-name-input" /></div>
          <div><Label className="text-xs">Start *</Label><Input type="time" value={f.start} onChange={(e) => setF({ ...f, start: e.target.value })} required data-testid="shift-start-input" /></div>
          <div><Label className="text-xs">End *</Label><Input type="time" value={f.end} onChange={(e) => setF({ ...f, end: e.target.value })} required data-testid="shift-end-input" /></div>
          {user.role === "SUPER_ADMIN" && (
            <div><Label className="text-xs">Temple *</Label>
              <Select value={f.temple_id} onValueChange={(v) => setF({ ...f, temple_id: v })}>
                <SelectTrigger data-testid="shift-temple-select"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{meta.temples.map((t) => <SelectItem key={t.temple_id} value={t.temple_id}>{t.name}</SelectItem>)}</SelectContent>
              </Select></div>
          )}
          <Button type="submit" className="bg-maroon-700 hover:bg-maroon-800 text-white" data-testid="shift-save-btn"><Plus className="w-4 h-4 mr-1" /> Add Shift</Button>
        </form>
      </CardContent></Card>
      <SimpleTable head={["Name", "Start", "End", "Temple", "Status"]} testid="shifts-table">
        {rows.map((s) => (
          <tr key={s.id} className="border-b border-[#F1EADB]">
            <td className="py-2 pr-4 font-semibold">{s.name}</td>
            <td className="py-2 pr-4">{s.start}</td>
            <td className="py-2 pr-4">{s.end}</td>
            <td className="py-2 pr-4">{s.temple_id}</td>
            <td className="py-2"><StatusBadge status={s.status} /></td>
          </tr>
        ))}
      </SimpleTable>
    </div>
  );
}

function DarshanTypes() {
  const { user } = useAuth();
  const [rows, load] = useList("/darshan-types");
  const [meta, setMeta] = useState({ temples: [] });
  const [f, setF] = useState({ name: "", code: "", amount: "", paid_free: "PAID", temple_id: "" });
  useEffect(() => { api.get("/meta/form-data").then((r) => setMeta(r.data)).catch(() => {}); }, []);
  const create = async (e) => {
    e.preventDefault();
    try { await api.post("/darshan-types", f); toast.success("Darshan type created"); setF({ name: "", code: "", amount: "", paid_free: "PAID", temple_id: "" }); load(); }
    catch (err) { toast.error(fmtErr(err)); }
  };
  return (
    <div className="space-y-3">
      <Card className="shadow-sm"><CardContent className="p-4">
        <form onSubmit={create} className="grid grid-cols-2 md:grid-cols-6 gap-3 items-end">
          <div><Label className="text-xs">Name *</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} required data-testid="darshan-name-input" /></div>
          <div><Label className="text-xs">Code</Label><Input value={f.code} onChange={(e) => setF({ ...f, code: e.target.value })} data-testid="darshan-code-input" /></div>
          <div><Label className="text-xs">Amount (₹)</Label><Input type="number" min={0} value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid="darshan-amount-input" /></div>
          <div><Label className="text-xs">Paid / Free</Label>
            <Select value={f.paid_free} onValueChange={(v) => setF({ ...f, paid_free: v })}>
              <SelectTrigger data-testid="darshan-paid-select"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="PAID">Paid</SelectItem><SelectItem value="FREE">Free</SelectItem></SelectContent>
            </Select></div>
          {user.role === "SUPER_ADMIN" && (
            <div><Label className="text-xs">Temple *</Label>
              <Select value={f.temple_id} onValueChange={(v) => setF({ ...f, temple_id: v })}>
                <SelectTrigger data-testid="darshan-temple-select"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{meta.temples.map((t) => <SelectItem key={t.temple_id} value={t.temple_id}>{t.name}</SelectItem>)}</SelectContent>
              </Select></div>
          )}
          <Button type="submit" className="bg-maroon-700 hover:bg-maroon-800 text-white" data-testid="darshan-save-btn"><Plus className="w-4 h-4 mr-1" /> Add</Button>
        </form>
      </CardContent></Card>
      <SimpleTable head={["Name", "Code", "Amount", "Paid/Free", "Temple", "Status"]} testid="darshan-table">
        {rows.map((d) => (
          <tr key={d.id} className="border-b border-[#F1EADB]">
            <td className="py-2 pr-4 font-semibold">{d.name}</td>
            <td className="py-2 pr-4 font-mono text-xs">{d.code}</td>
            <td className="py-2 pr-4">₹{d.amount}</td>
            <td className="py-2 pr-4">{d.paid_free}</td>
            <td className="py-2 pr-4">{d.temple_id}</td>
            <td className="py-2"><StatusBadge status={d.status} /></td>
          </tr>
        ))}
      </SimpleTable>
    </div>
  );
}

function Sevas() {
  const { user } = useAuth();
  const [rows, load] = useList("/sevas");
  const [meta, setMeta] = useState({ temples: [] });
  const [f, setF] = useState({ name: "", name_telugu: "", amount: "", max_devotees: 2, temple_id: "" });
  useEffect(() => { api.get("/meta/form-data").then((r) => setMeta(r.data)).catch(() => {}); }, []);
  const create = async (e) => {
    e.preventDefault();
    try { await api.post("/sevas", f); toast.success("Seva created"); setF({ name: "", name_telugu: "", amount: "", max_devotees: 2, temple_id: "" }); load(); }
    catch (err) { toast.error(fmtErr(err)); }
  };
  const [editSeva, setEditSeva] = useState(null);
  const [editF, setEditF] = useState({});
  const openEdit = (s) => { setEditSeva(s); setEditF({ name: s.name, name_telugu: s.name_telugu, amount: s.amount, max_devotees: s.max_devotees, reason: "" }); };
  const saveEdit = async () => {
    try { await api.put(`/sevas/${editSeva.id}`, editF); toast.success("Seva updated (audited)"); setEditSeva(null); load(); }
    catch (err) { toast.error(fmtErr(err)); }
  };
  const remove = async (s) => {
    if (!window.confirm(`Deactivate seva "${s.name}"? (soft delete — record is preserved and audited)`)) return;
    try { await api.delete(`/sevas/${s.id}`); toast.success("Seva deactivated"); load(); }
    catch (err) { toast.error(fmtErr(err)); }
  };
  const restore = async (s) => {
    try { await api.put(`/sevas/${s.id}`, { status: "ACTIVE" }); toast.success("Seva restored"); load(); }
    catch (err) { toast.error(fmtErr(err)); }
  };
  return (
    <div className="space-y-3">
      <Card className="shadow-sm"><CardContent className="p-4">
        <form onSubmit={create} className="grid grid-cols-2 md:grid-cols-6 gap-3 items-end">
          <div><Label className="text-xs">Seva Name *</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} required data-testid="seva-name-input" /></div>
          <div><Label className="text-xs">Telugu Name</Label><Input value={f.name_telugu} onChange={(e) => setF({ ...f, name_telugu: e.target.value })} /></div>
          <div><Label className="text-xs">Amount (₹)</Label><Input type="number" min={0} value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid="seva-amount-input" /></div>
          <div><Label className="text-xs">Max Devotees</Label><Input type="number" min={1} value={f.max_devotees} onChange={(e) => setF({ ...f, max_devotees: e.target.value })} /></div>
          {user.role === "SUPER_ADMIN" && (
            <div><Label className="text-xs">Temple *</Label>
              <Select value={f.temple_id} onValueChange={(v) => setF({ ...f, temple_id: v })}>
                <SelectTrigger data-testid="seva-temple-select"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{meta.temples.map((t) => <SelectItem key={t.temple_id} value={t.temple_id}>{t.name}</SelectItem>)}</SelectContent>
              </Select></div>
          )}
          <Button type="submit" className="bg-maroon-700 hover:bg-maroon-800 text-white" data-testid="seva-save-btn"><Plus className="w-4 h-4 mr-1" /> Add</Button>
        </form>
      </CardContent></Card>
      <SimpleTable head={["Seva", "Telugu", "Amount", "Max Devotees", "Temple", "Status", "Actions"]} testid="sevas-table">
        {rows.map((s) => (
          <tr key={s.id} className="border-b border-[#F1EADB]" data-testid={`seva-row-${s.id}`}>
            <td className="py-2 pr-4 font-semibold">{s.name}</td>
            <td className="py-2 pr-4">{s.name_telugu}</td>
            <td className="py-2 pr-4">₹{s.amount}</td>
            <td className="py-2 pr-4">{s.max_devotees}</td>
            <td className="py-2 pr-4">{s.temple_id}</td>
            <td className="py-2 pr-4"><StatusBadge status={s.status} /></td>
            <td className="py-2 flex gap-1">
              <Button size="sm" variant="ghost" title="Edit seva" data-testid={`seva-edit-${s.id}`} onClick={() => openEdit(s)}><Pencil className="w-4 h-4 text-saffron-600" /></Button>
              {s.status === "ACTIVE" ? (
                <Button size="sm" variant="ghost" title="Deactivate seva" data-testid={`seva-delete-${s.id}`} onClick={() => remove(s)}><Trash2 className="w-4 h-4 text-red-600" /></Button>
              ) : (
                <Button size="sm" variant="ghost" title="Restore seva" data-testid={`seva-restore-${s.id}`} onClick={() => restore(s)}><RotateCcw className="w-4 h-4 text-green-700" /></Button>
              )}
            </td>
          </tr>
        ))}
      </SimpleTable>

      <Dialog open={!!editSeva} onOpenChange={() => setEditSeva(null)}>
        <DialogContent data-testid="seva-edit-dialog">
          <DialogHeader><DialogTitle className="font-heading text-maroon-900">Edit Seva</DialogTitle></DialogHeader>
          {editSeva && (
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Seva Name *</Label><Input value={editF.name || ""} onChange={(e) => setEditF({ ...editF, name: e.target.value })} data-testid="seva-edit-name" /></div>
              <div><Label className="text-xs">Telugu Name</Label><Input value={editF.name_telugu || ""} onChange={(e) => setEditF({ ...editF, name_telugu: e.target.value })} /></div>
              <div><Label className="text-xs">Amount (₹)</Label><Input type="number" min={0} value={editF.amount ?? 0} onChange={(e) => setEditF({ ...editF, amount: e.target.value })} data-testid="seva-edit-amount" /></div>
              <div><Label className="text-xs">Max Devotees</Label><Input type="number" min={1} value={editF.max_devotees ?? 2} onChange={(e) => setEditF({ ...editF, max_devotees: e.target.value })} /></div>
              <div className="col-span-2"><Label className="text-xs">Reason (optional)</Label><Input value={editF.reason || ""} onChange={(e) => setEditF({ ...editF, reason: e.target.value })} data-testid="seva-edit-reason" /></div>
              <div className="col-span-2 flex justify-end gap-2">
                <Button variant="outline" onClick={() => setEditSeva(null)}>Close</Button>
                <Button onClick={saveEdit} className="bg-maroon-700 hover:bg-maroon-800 text-white" data-testid="seva-edit-save-btn">Save Changes</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SareeTypes() {
  const { user } = useAuth();
  const [rows, load] = useList("/saree-types");
  const [meta, setMeta] = useState({ temples: [] });
  const [f, setF] = useState({ name: "", code: "", rate: "", monthly_quota: "", hsn_code: "52082120", gst_percent: 5, temple_id: "" });
  const [editSt, setEditSt] = useState(null);
  const [efSt, setEfSt] = useState({});
  const [rateEdit, setRateEdit] = useState(null);
  const saveRate = async (s) => {
    const v = Number(rateEdit.value);
    if (isNaN(v) || v < 0) { toast.error("Invalid rate"); setRateEdit(null); return; }
    try { await api.put(`/saree-types/${s.id}`, { name: s.name, rate: v, monthly_quota: s.monthly_quota || 0 }); toast.success("Rate updated (audited)"); load(); }
    catch (err) { toast.error(fmtErr(err)); }
    setRateEdit(null);
  };
  const openEditSt = (s) => { setEditSt(s); setEfSt({ name: s.name, code: s.code || "", rate: s.rate, monthly_quota: s.monthly_quota || 0, date_quota: !!s.date_quota,
    hsn_code: s.hsn_code || "52082120", gst_percent: s.gst_percent ?? 5 }); };
  const saveEditSt = async () => {
    try { await api.put(`/saree-types/${editSt.id}`, efSt); toast.success("Saree type updated (audited)"); setEditSt(null); load(); }
    catch (err) { toast.error(fmtErr(err)); }
  };
  useEffect(() => { api.get("/meta/form-data").then((r) => setMeta(r.data)).catch(() => {}); }, []);
  const create = async (e) => {
    e.preventDefault();
    try { await api.post("/saree-types", f); toast.success("Saree type created"); setF({ name: "", code: "", rate: "", monthly_quota: "", hsn_code: "52082120", gst_percent: 5, temple_id: "" }); load(); }
    catch (err) { toast.error(fmtErr(err)); }
  };
  const remove = async (s) => {
    if (!window.confirm(`Deactivate saree type "${s.name}"?`)) return;
    try { await api.delete(`/saree-types/${s.id}`); toast.success("Deactivated"); load(); }
    catch (err) { toast.error(fmtErr(err)); }
  };
  return (
    <div className="space-y-3">
      <Card className="shadow-sm"><CardContent className="p-4">
        <form onSubmit={create} className="grid grid-cols-2 md:grid-cols-4 gap-3 items-end">
          <div><Label className="text-xs">Saree Type Name *</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} required data-testid="sareetype-name-input" /></div>
          <div><Label className="text-xs">Code (token prefix)</Label><Input value={f.code} maxLength={6} onChange={(e) => setF({ ...f, code: e.target.value.toUpperCase() })} placeholder="e.g. PAT" data-testid="sareetype-code-input" /></div>
          <div><Label className="text-xs">Rate (₹)</Label><Input type="number" min={0} value={f.rate} onChange={(e) => setF({ ...f, rate: e.target.value })} data-testid="sareetype-rate-input" /></div>
          <div><Label className="text-xs">Monthly Quota (0 = unlimited)</Label><Input type="number" min={0} value={f.monthly_quota} onChange={(e) => setF({ ...f, monthly_quota: e.target.value })} data-testid="sareetype-quota-input" placeholder="e.g. 15" /></div>
          <div><Label className="text-xs">HSN Code (sale)</Label><Input value={f.hsn_code} maxLength={8} inputMode="numeric" onChange={(e) => setF({ ...f, hsn_code: e.target.value.replace(/\D/g, "") })} data-testid="sareetype-hsn-input" /></div>
          <div><Label className="text-xs">GST % (sale)</Label><Input type="number" min={0} max={28} step="0.5" value={f.gst_percent} onChange={(e) => setF({ ...f, gst_percent: e.target.value })} data-testid="sareetype-gst-input" /></div>
          {user.role === "SUPER_ADMIN" && (
            <div><Label className="text-xs">Temple *</Label>
              <Select value={f.temple_id} onValueChange={(v) => setF({ ...f, temple_id: v })}>
                <SelectTrigger data-testid="sareetype-temple-select"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{meta.temples.map((t) => <SelectItem key={t.temple_id} value={t.temple_id}>{t.name}</SelectItem>)}</SelectContent>
              </Select></div>
          )}
          <Button type="submit" className="bg-maroon-700 hover:bg-maroon-800 text-white" data-testid="sareetype-save-btn"><Plus className="w-4 h-4 mr-1" /> Add</Button>
        </form>
      </CardContent></Card>
      <SimpleTable head={["Name", "Code", "Rate", "HSN / GST", "Monthly Quota", "Temple", "Status", "Actions"]} testid="sareetypes-table">
        {rows.map((s) => (
          <tr key={s.id} className="border-b border-[#F1EADB]" data-testid={`sareetype-row-${s.id}`}>
            <td className="py-2 pr-4 font-semibold">{s.name}</td>
            <td className="py-2 pr-4 font-mono text-xs">{s.code || "-"}</td>
            <td className="py-2 pr-4" data-testid={`sareetype-rate-cell-${s.id}`}>
              {rateEdit?.id === s.id ? (
                <Input type="number" min={0} autoFocus className="h-8 w-24" value={rateEdit.value}
                  onChange={(e) => setRateEdit({ id: s.id, value: e.target.value })}
                  onBlur={() => saveRate(s)}
                  onKeyDown={(e) => { if (e.key === "Enter") saveRate(s); if (e.key === "Escape") setRateEdit(null); }}
                  data-testid={`sareetype-rate-inline-${s.id}`} />
              ) : (
                <button type="button" className="hover:bg-gold-50 rounded px-1 -mx-1 cursor-text font-semibold" title="Click to edit rate"
                  onClick={() => setRateEdit({ id: s.id, value: s.rate })} data-testid={`sareetype-rate-edit-${s.id}`}>₹{s.rate}</button>
              )}
            </td>
            <td className="py-2 pr-4 text-xs" data-testid={`sareetype-hsn-gst-${s.id}`}><span className="font-mono">{s.hsn_code || "52082120"}</span> · {s.gst_percent ?? 5}%</td>
            <td className="py-2 pr-4" data-testid={`sareetype-quota-${s.id}`}>{s.monthly_quota > 0 ? s.monthly_quota : "∞"}
              {s.date_quota && <span className="ml-2 text-[10px] font-bold text-maroon-800 bg-gold-50 border border-gold-300 rounded px-1.5 py-0.5">DATE-WISE</span>}</td>
            <td className="py-2 pr-4">{s.temple_id}</td>
            <td className="py-2 pr-4"><StatusBadge status={s.status} /></td>
            <td className="py-2 flex gap-1">
              <Button size="sm" variant="ghost" title="Edit" data-testid={`sareetype-edit-${s.id}`} onClick={() => openEditSt(s)}><Pencil className="w-4 h-4 text-saffron-600" /></Button>
              {s.status === "ACTIVE" && (
                <Button size="sm" variant="ghost" title="Deactivate" data-testid={`sareetype-delete-${s.id}`} onClick={() => remove(s)}><Trash2 className="w-4 h-4 text-red-600" /></Button>
              )}
            </td>
          </tr>
        ))}
      </SimpleTable>

      <Dialog open={!!editSt} onOpenChange={() => setEditSt(null)}>
        <DialogContent data-testid="sareetype-edit-dialog">
          <DialogHeader><DialogTitle className="font-heading text-maroon-900">Edit Saree Type</DialogTitle></DialogHeader>
          {editSt && (
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Name *</Label><Input value={efSt.name || ""} onChange={(e) => setEfSt({ ...efSt, name: e.target.value })} data-testid="sareetype-edit-name" /></div>
              <div><Label className="text-xs">Code (token prefix)</Label><Input value={efSt.code || ""} maxLength={6} onChange={(e) => setEfSt({ ...efSt, code: e.target.value.toUpperCase() })} placeholder="e.g. PAT" data-testid="sareetype-edit-code" /></div>
              <div><Label className="text-xs">Rate (₹)</Label><Input type="number" min={0} value={efSt.rate ?? 0} onChange={(e) => setEfSt({ ...efSt, rate: e.target.value })} data-testid="sareetype-edit-rate" /></div>
              <div><Label className="text-xs">Monthly Quota (0 = unlimited)</Label><Input type="number" min={0} value={efSt.monthly_quota ?? 0} onChange={(e) => setEfSt({ ...efSt, monthly_quota: e.target.value })} data-testid="sareetype-edit-quota" placeholder="e.g. 15 per month" /></div>
              <div><Label className="text-xs">HSN Code (sale)</Label><Input value={efSt.hsn_code || ""} maxLength={8} inputMode="numeric" onChange={(e) => setEfSt({ ...efSt, hsn_code: e.target.value.replace(/\D/g, "") })} data-testid="sareetype-edit-hsn" /></div>
              <div><Label className="text-xs">GST % (sale)</Label><Input type="number" min={0} max={28} step="0.5" value={efSt.gst_percent ?? 5} onChange={(e) => setEfSt({ ...efSt, gst_percent: e.target.value })} data-testid="sareetype-edit-gst" /></div>
              <label className="col-span-2 flex items-center gap-2 text-sm" data-testid="sareetype-edit-date-quota">
                <input type="checkbox" checked={!!efSt.date_quota} onChange={(e) => setEfSt({ ...efSt, date_quota: e.target.checked })} />
                Date-wise quota by Booking Date (release dates in Masters → Saree Date Quotas)
              </label>
              <div className="flex items-end justify-end gap-2">
                <Button variant="outline" onClick={() => setEditSt(null)}>Close</Button>
                <Button onClick={saveEditSt} className="bg-maroon-700 hover:bg-maroon-800 text-white" data-testid="sareetype-edit-save-btn">Save</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const ddmmyyyy = (d) => (d ? `${d.slice(8, 10)}/${d.slice(5, 7)}/${d.slice(0, 4)}` : "");

// Super Admin / Temple Admin release saree quotas per booking date (e.g. Pattu Saree: 7 on 24/09/2026).
function SareeDateQuotas() {
  const { user } = useAuth();
  const [types, setTypes] = useState([]);
  const [typeId, setTypeId] = useState("");
  const [range, setRange] = useState({ frm: "", to: "" });
  const [f, setF] = useState({ frm: "", to: "", quota: "" });
  const [rows, setRows] = useState([]);
  const [edit, setEdit] = useState(null);

  useEffect(() => { api.get("/saree-types").then((r) => setTypes(r.data.filter((t) => t.status === "ACTIVE"))).catch(() => {}); }, []);
  const load = (id = typeId) => {
    if (!id) { setRows([]); return; }
    const q = new URLSearchParams({ saree_type_id: id });
    if (range.frm) q.set("frm", range.frm);
    if (range.to) q.set("to", range.to);
    api.get(`/saree-date-quotas?${q}`).then((r) => setRows(r.data)).catch((e) => toast.error(fmtErr(e)));
  };
  const type = types.find((t) => t.id === typeId);

  const release = async (e) => {
    e.preventDefault();
    if (!typeId || !f.frm || f.quota === "") { toast.error("Select saree type, booking date and quota"); return; }
    try {
      const r = await api.post("/saree-date-quotas", { saree_type_id: typeId, frm: f.frm, to: f.to || f.frm, quota: Number(f.quota) });
      const d = r.data;
      toast.success(`Quota released: ${d.released} new date(s), ${d.updated} updated${d.date_quota_enabled ? ` — date-wise quota is now ON for ${type?.name}` : ""}`);
      if (d.skipped.length) toast.error(`Not changed: ${d.skipped.join(", ")}`);
      api.get("/saree-types").then((x) => setTypes(x.data.filter((t) => t.status === "ACTIVE"))).catch(() => {});
      load();
    } catch (err) { toast.error(fmtErr(err)); }
  };
  const saveEdit = async () => {
    try { await api.put(`/saree-date-quotas/${edit.id}`, { quota: Number(edit.quota) }); toast.success("Quota updated (audited)"); setEdit(null); load(); }
    catch (err) { toast.error(fmtErr(err)); }
  };
  const remove = async (r) => {
    if (!window.confirm(`Remove the quota for ${ddmmyyyy(r.date)}? Bookings for that date will be blocked.`)) return;
    try { await api.delete(`/saree-date-quotas/${r.id}`); toast.success("Removed"); load(); }
    catch (err) { toast.error(fmtErr(err)); }
  };

  return (
    <div className="space-y-3">
      <Card className="shadow-sm"><CardContent className="p-4 space-y-3">
        <p className="text-xs text-slate-500">Release saree quotas by <b>Booking Date</b>. When a saree type has date-wise quota ON, the Receiving Counter can book only on released dates, up to the quota; once full, it shows "Quota Full" and suggests the next available date. Only Super Admin and Temple Admin can manage quotas.</p>
        <form onSubmit={release} className="grid grid-cols-2 md:grid-cols-5 gap-3 items-end">
          <div className="col-span-2 md:col-span-1"><Label className="text-xs">Saree Type *</Label>
            <Select value={typeId} onValueChange={(v) => { setTypeId(v); load(v); }}>
              <SelectTrigger data-testid="dq-type"><SelectValue placeholder="Select (e.g. Pattu Saree)" /></SelectTrigger>
              <SelectContent>{types.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}{user.role === "SUPER_ADMIN" ? ` · ${t.temple_id}` : ""}{t.date_quota ? " · date-wise" : ""}</SelectItem>)}</SelectContent>
            </Select></div>
          <div><Label className="text-xs">Booking Date (from) *</Label><Input type="date" value={f.frm} onChange={(e) => setF({ ...f, frm: e.target.value })} data-testid="dq-from" /></div>
          <div><Label className="text-xs">To (optional, same quota each day)</Label><Input type="date" value={f.to} min={f.frm} onChange={(e) => setF({ ...f, to: e.target.value })} data-testid="dq-to" /></div>
          <div><Label className="text-xs">Quota per date *</Label><Input type="number" min={0} value={f.quota} onChange={(e) => setF({ ...f, quota: e.target.value })} placeholder="e.g. 7" data-testid="dq-quota" /></div>
          <Button type="submit" className="bg-maroon-700 hover:bg-maroon-800 text-white" data-testid="dq-release-btn"><Plus className="w-4 h-4 mr-1" /> Release Quota</Button>
        </form>
        {type && <p className="text-xs" data-testid="dq-type-status">{type.name}: date-wise quota is <b className={type.date_quota ? "text-green-700" : "text-slate-500"}>{type.date_quota ? "ON" : "OFF (turns ON when you release the first date)"}</b></p>}
      </CardContent></Card>

      {typeId && (
        <Card className="shadow-sm"><CardContent className="p-4">
          <div className="flex flex-wrap gap-3 items-end mb-3">
            <div><Label className="text-xs">Show from</Label><Input type="date" value={range.frm} onChange={(e) => setRange({ ...range, frm: e.target.value })} /></div>
            <div><Label className="text-xs">to</Label><Input type="date" value={range.to} onChange={(e) => setRange({ ...range, to: e.target.value })} /></div>
            <Button variant="outline" onClick={() => load()}>Show</Button>
          </div>
          <SimpleTable head={["Booking Date", "Total Quota", "Booked", "Remaining", "Status", "Actions"]} testid="dq-table">
            {rows.length === 0 && <tr><td colSpan={6} className="py-6 text-center text-slate-400">No quota released for this saree type yet</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} className="border-b border-[#F1EADB]" data-testid={`dq-row-${r.date}`}>
                <td className="py-2 pr-4 font-semibold">{ddmmyyyy(r.date)}</td>
                <td className="py-2 pr-4">{edit?.id === r.id ? (
                  <Input type="number" min={r.booked} autoFocus className="h-8 w-24" value={edit.quota}
                    onChange={(e) => setEdit({ ...edit, quota: e.target.value })}
                    onKeyDown={(e) => { if (e.key === "Enter") saveEdit(); if (e.key === "Escape") setEdit(null); }} />
                ) : r.quota}</td>
                <td className="py-2 pr-4">{r.booked}</td>
                <td className="py-2 pr-4">{r.remaining}</td>
                <td className="py-2 pr-4">{r.status === "QUOTA_FULL"
                  ? <span className="text-xs font-bold text-red-700 bg-red-50 border border-red-200 rounded px-2 py-0.5">QUOTA FULL</span>
                  : <span className="text-xs font-bold text-green-700 bg-green-50 border border-green-200 rounded px-2 py-0.5">AVAILABLE</span>}</td>
                <td className="py-2 flex gap-1">
                  {edit?.id === r.id
                    ? <><Button size="sm" onClick={saveEdit} className="bg-maroon-700 text-white">Save</Button><Button size="sm" variant="ghost" onClick={() => setEdit(null)}>Cancel</Button></>
                    : <Button size="sm" variant="ghost" title="Change quota" onClick={() => setEdit({ id: r.id, quota: r.quota })}><Pencil className="w-4 h-4 text-saffron-600" /></Button>}
                  {!r.booked && <Button size="sm" variant="ghost" title="Remove" onClick={() => remove(r)}><Trash2 className="w-4 h-4 text-red-600" /></Button>}
                </td>
              </tr>
            ))}
          </SimpleTable>
        </CardContent></Card>
      )}
    </div>
  );
}

// Super Admin: WhatsApp number + approved Authentication template used for Forgot Password OTPs.
function WhatsAppOtpSettings() {
  const [c, setC] = useState({ wa_phone_number_id: "", wa_access_token: "", template: "otp", lang: "en_US" });
  const load = () => api.get("/settings/whatsapp-otp").then((r) => setC(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const save = async () => {
    try { await api.put("/settings/whatsapp-otp", c); toast.success("WhatsApp OTP settings saved (audited)"); load(); }
    catch (err) { toast.error(fmtErr(err)); }
  };
  return (
    <Card className="shadow-sm mt-4"><CardContent className="p-4 max-w-xl space-y-3" data-testid="wa-otp-settings">
      <p className="text-xs font-bold uppercase tracking-wider text-gold-700">WhatsApp OTP — Forgot Password</p>
      <p className="text-xs text-slate-500">Uses your approved Meta <b>Authentication</b> template (e.g. <span className="font-mono">otp</span>, "123456 is your verification code" with Copy code button). Leave Phone Number ID / Token blank to use each user's temple WhatsApp number. Users need a mobile number in Masters → Users.</p>
      <div className="grid grid-cols-2 gap-3">
        <div><Label className="text-xs">Phone Number ID</Label><Input value={c.wa_phone_number_id} onChange={(e) => setC({ ...c, wa_phone_number_id: e.target.value })} placeholder="from Meta → API Setup" /></div>
        <div><Label className="text-xs">Access Token</Label><Input value={c.wa_access_token} onChange={(e) => setC({ ...c, wa_access_token: e.target.value })} placeholder="permanent token" /></div>
        <div><Label className="text-xs">OTP Template Name</Label><Input value={c.template} onChange={(e) => setC({ ...c, template: e.target.value })} placeholder="otp" /></div>
        <div><Label className="text-xs">Language code</Label><Input value={c.lang} onChange={(e) => setC({ ...c, lang: e.target.value })} placeholder="en_US" /></div>
      </div>
      <Button onClick={save} className="bg-maroon-700 hover:bg-maroon-800 text-white">Save OTP Settings</Button>
    </CardContent></Card>
  );
}

function Branding() {
  const [b, setB] = useState({ header_title: "", footer_text: "", logo: "" });
  const load = () => api.get("/settings/branding").then((r) => setB(r.data)).catch((e) => toast.error(fmtErr(e)));
  useEffect(() => { load(); }, []);
  const save = async () => {
    try { await api.put("/settings/branding", b); toast.success("Branding updated (audited)"); load(); }
    catch (err) { toast.error(fmtErr(err)); }
  };
  return (
    <Card className="shadow-sm"><CardContent className="p-4 max-w-xl space-y-4">
      <p className="text-xs text-slate-500" data-testid="branding-hint">App-wide header logo, title and footer text. Individual temples can override these (Edit Temple → App Branding Override).</p>
      <div><Label className="text-xs">Header Title</Label><Input value={b.header_title} onChange={(e) => setB({ ...b, header_title: e.target.value })} data-testid="branding-header-title" /></div>
      <div><Label className="text-xs">Footer Text</Label><Input value={b.footer_text} onChange={(e) => setB({ ...b, footer_text: e.target.value })} data-testid="branding-footer-text" /></div>
      <div>
        <Label className="text-xs">Header Logo (PNG/JPG, max 1 MB)</Label>
        <div className="flex items-center gap-3 mt-1">
          {b.logo && <img src={b.logo} alt="logo" className="w-10 h-10 object-contain border border-gold-300 rounded" data-testid="branding-logo-preview" />}
          <label className="text-xs text-maroon-700 underline cursor-pointer" data-testid="branding-logo-upload">
            {b.logo ? "Replace logo" : "Upload logo"}
            <input type="file" accept="image/png,image/jpeg" className="hidden" onChange={(e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              if (file.size > 1024 * 1024) { toast.error("Logo too large (max 1 MB)"); return; }
              const reader = new FileReader();
              reader.onload = () => setB((p) => ({ ...p, logo: reader.result }));
              reader.readAsDataURL(file);
            }} />
          </label>
          {b.logo && <button type="button" className="text-xs text-red-600 underline" onClick={() => setB({ ...b, logo: "" })} data-testid="branding-logo-remove">Remove</button>}
        </div>
      </div>
      <Button onClick={save} className="bg-maroon-700 hover:bg-maroon-800 text-white" data-testid="branding-save-btn">Save Branding</Button>
    </CardContent></Card>
  );
}

export default function Masters() {
  const { user } = useAuth();
  return (
    <div data-testid="masters-page">
      <h1 className="font-heading text-2xl sm:text-3xl font-bold text-maroon-900 mb-5">Masters</h1>
      <Tabs defaultValue="temples">
        <TabsList className="bg-white border border-[#E5D9C5] flex-wrap h-auto">
          <TabsTrigger value="temples" data-testid="tab-temples">Temples</TabsTrigger>
          <TabsTrigger value="users" data-testid="tab-users">Users</TabsTrigger>
          <TabsTrigger value="counters" data-testid="tab-counters">Counters</TabsTrigger>
          <TabsTrigger value="shifts" data-testid="tab-shifts">Shifts</TabsTrigger>
          <TabsTrigger value="darshan" data-testid="tab-darshan">Darshan Types</TabsTrigger>
          <TabsTrigger value="sevas" data-testid="tab-sevas">Sevas</TabsTrigger>
          <TabsTrigger value="sarees" data-testid="tab-sarees">Saree Types</TabsTrigger>
          <TabsTrigger value="saree-quotas" data-testid="tab-saree-quotas">Saree Date Quotas</TabsTrigger>
          {user.role === "SUPER_ADMIN" && <TabsTrigger value="branding" data-testid="tab-branding">Branding</TabsTrigger>}
        </TabsList>
        <TabsContent value="temples" className="mt-4"><Temples /></TabsContent>
        <TabsContent value="users" className="mt-4"><Users /></TabsContent>
        <TabsContent value="counters" className="mt-4"><Counters /></TabsContent>
        <TabsContent value="shifts" className="mt-4"><Shifts /></TabsContent>
        <TabsContent value="darshan" className="mt-4"><DarshanTypes /></TabsContent>
        <TabsContent value="sevas" className="mt-4"><Sevas /></TabsContent>
        <TabsContent value="sarees" className="mt-4"><SareeTypes /></TabsContent>
        <TabsContent value="saree-quotas" className="mt-4"><SareeDateQuotas /></TabsContent>
        {user.role === "SUPER_ADMIN" && <TabsContent value="branding" className="mt-4"><Branding /><WhatsAppOtpSettings /></TabsContent>}
      </Tabs>
    </div>
  );
}
